/**
 * Diálogo de cobro de un pedido. Casos soportados:
 *   - Pago mixto: se registran varios pagos (efectivo + Nequi…) hasta cubrir
 *     el total y luego "Finalizar cobro".
 *   - Abonos con pedido abierto: se puede cerrar el diálogo con saldo
 *     pendiente y seguir cobrando después (cada pago cuenta en la caja de
 *     quien lo recibe).
 *   - Vuelto: en efectivo, "Recibido" calcula el cambio; solo se registra lo
 *     aplicado al pedido.
 *   - Fiado (feature `customer_credit` + permiso `payments.credit`): parte o
 *     todo el saldo queda a nombre de un cliente. Un deudor por pedido.
 *   - Anular un pago mal registrado (permiso `payments.void`).
 *
 * El worker valida todo de nuevo (monto ≤ pendiente, caja abierta, permisos).
 */

import type { PaymentMethod } from '@atreus/core';
import { Button, cn } from '@atreus/ui';
import { Check, CreditCard, Trash2, UserRound, X } from 'lucide-react';
import { useState } from 'react';

import { useConfirm } from '../../components/ConfirmDialog.js';
import { useFeatures } from '../../lib/features.js';
import { formatCOP, parseCOPToCents } from '../../lib/format.js';
import { paymentMethodLabel } from '../../lib/payments.js';
import { usePermissions } from '../../lib/permissions.js';
import { trpc } from '../../lib/trpc.js';
import { CustomerPicker, type PickedCustomer } from '../customers/CustomerPicker.js';

const MONEY_METHODS: readonly PaymentMethod[] = ['cash', 'card', 'transfer', 'nequi', 'daviplata'];

const SPLITS = [
  { label: 'Resto', divisor: 1 },
  { label: '½', divisor: 2 },
  { label: '⅓', divisor: 3 },
] as const;

interface DialogCustomer {
  id: string;
  fullName: string;
  balanceCents: number | null;
}

export const PayDialog = ({ orderId, onClose }: { orderId: string; onClose: () => void }) => {
  const utils = trpc.useUtils();
  const summary = trpc.payments.summary.useQuery({ orderId });
  const existing = trpc.payments.listByOrder.useQuery({ orderId });
  const features = useFeatures();
  const permissions = usePermissions();
  const { confirm, dialog } = useConfirm();

  const creditAllowed =
    features.isEnabled('customer_credit') && permissions.canDo('payments.credit');
  const canVoid = permissions.canDo('payments.void');

  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [amountInput, setAmountInput] = useState('');
  const [receivedInput, setReceivedInput] = useState('');
  const [pickedCustomer, setPickedCustomer] = useState<PickedCustomer | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const invalidate = () => {
    void utils.payments.summary.invalidate({ orderId });
    void utils.payments.listByOrder.invalidate({ orderId });
    void utils.orders.getById.invalidate({ id: orderId });
    void utils.orders.list.invalidate();
    void utils.cash.summary.invalidate();
    void utils.cash.listMovements.invalidate();
    void utils.cash.sales.invalidate();
    void utils.customers.list.invalidate();
    void utils.customers.get.invalidate();
  };

  const createPayment = trpc.payments.create.useMutation({
    onSuccess: () => {
      invalidate();
      setAmountInput('');
      setReceivedInput('');
    },
  });
  const voidPayment = trpc.payments.void.useMutation({ onSuccess: invalidate });
  const transition = trpc.orders.transition.useMutation({
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  // Si el pedido ya tiene fiado, el deudor queda fijo (regla del worker).
  const locked = summary.data?.customer ?? null;
  const customer: DialogCustomer | null = locked
    ? {
        id: locked.id,
        fullName: locked.fullName,
        balanceCents: pickedCustomer?.id === locked.id ? pickedCustomer.balanceCents : null,
      }
    : pickedCustomer;

  const remaining = summary.data?.remainingCents ?? 0;
  const requested = parseCOPToCents(amountInput);
  const baseAmount = requested > 0 ? requested : remaining;
  const receivedCents = method === 'cash' ? parseCOPToCents(receivedInput) : 0;
  const amountToCharge = receivedCents > 0 ? Math.min(receivedCents, baseAmount) : baseAmount;
  const changeCents = receivedCents > baseAmount ? receivedCents - baseAmount : 0;
  const exceeds = baseAmount > remaining;
  const needsCustomer = method === 'credit' && !customer;
  const canSubmit =
    !createPayment.isPending &&
    remaining > 0 &&
    amountToCharge > 0 &&
    !exceeds &&
    !needsCustomer;

  const selectMethod = (m: PaymentMethod): void => {
    setMethod(m);
    if (m === 'credit' && !customer) setPickerOpen(true);
    if (m !== 'credit') setPickerOpen(false);
  };

  const submit = (): void => {
    createPayment.mutate({
      orderId,
      method,
      amountCents: amountToCharge,
      ...(method === 'credit' && customer ? { customerId: customer.id } : {}),
      // Efectivo entregado → el recibo muestra "Recibido" y "Cambio".
      ...(method === 'cash' && receivedCents > 0 ? { tenderedCents: receivedCents } : {}),
    });
  };

  const handleVoid = async (paymentId: string, label: string): Promise<void> => {
    const ok = await confirm({
      title: '¿Anular este pago?',
      message: `${label}. Si fue en efectivo se registra la devolución en caja.`,
      confirmLabel: 'Anular pago',
      variant: 'danger',
    });
    if (ok) voidPayment.mutate({ paymentId });
  };

  const splitAmount = (divisor: number): void => {
    if (divisor === 1) {
      setAmountInput('');
      return;
    }
    // Pesos redondeados hacia arriba: con ⅓ el último abono cubre el resto.
    setAmountInput(String(Math.ceil(remaining / divisor / 100)));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pay-dialog-title"
    >
      {dialog}
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-lg border border-border bg-surface-raised shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 id="pay-dialog-title" className="text-lg font-black tracking-tight">
            Cobrar pedido
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Cerrar">
            <X className="size-4" />
          </Button>
        </header>

        <div className="space-y-4 overflow-y-auto p-5">
          <div className="rounded-md border border-border bg-surface p-3 text-sm">
            <SummaryLine label="Total" value={formatCOP(summary.data?.totalCents ?? 0)} strong />
            <SummaryLine label="Pagado" value={formatCOP(summary.data?.paidCents ?? 0)} />
            {(summary.data?.creditCents ?? 0) > 0 ? (
              <SummaryLine
                label={`Crédito${locked ? ` · ${locked.fullName}` : ''}`}
                value={formatCOP(summary.data?.creditCents ?? 0)}
              />
            ) : null}
            <div className="mt-1 flex items-baseline justify-between border-t border-border pt-1">
              <span className="font-bold">Pendiente</span>
              <span className="text-xl font-black tabular-nums">{formatCOP(remaining)}</span>
            </div>
          </div>

          {existing.data && existing.data.length > 0 ? (
            <ul className="space-y-1 text-xs" aria-label="Pagos registrados">
              {existing.data.map((p) => {
                const label = `${paymentMethodLabel(p.method)} ${formatCOP(p.amountCents)}`;
                return (
                  <li key={p.id} className="flex items-center justify-between gap-2 text-fg-muted">
                    <span>
                      {paymentMethodLabel(p.method)}
                      {p.tenderedCents != null && p.tenderedCents > p.amountCents
                        ? ` · recibido ${formatCOP(p.tenderedCents)} · cambio ${formatCOP(p.tenderedCents - p.amountCents)}`
                        : ''}
                    </span>
                    <span className="ml-auto tabular-nums">{formatCOP(p.amountCents)}</span>
                    {canVoid ? (
                      <button
                        type="button"
                        onClick={() => void handleVoid(p.id, label)}
                        disabled={voidPayment.isPending}
                        className="rounded p-1 text-fg-subtle hover:bg-danger/10 hover:text-danger disabled:opacity-50"
                        aria-label={`Anular pago ${label}`}
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {voidPayment.error ? (
            <p className="text-sm text-danger">{voidPayment.error.message}</p>
          ) : null}

          {summary.isLoading ? (
            <p className="text-sm text-fg-muted">Cargando…</p>
          ) : remaining > 0 ? (
            <>
              <div>
                <span className="mb-2 block text-sm font-semibold">Método</span>
                <div className="grid grid-cols-3 gap-2">
                  {[...MONEY_METHODS, ...(creditAllowed ? (['credit'] as const) : [])].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => selectMethod(m)}
                      aria-pressed={method === m}
                      className={cn(
                        'rounded-md border px-3 py-2 text-sm font-semibold transition-colors',
                        method === m
                          ? 'border-fg bg-fg text-fg-inverse'
                          : 'border-border bg-surface text-fg hover:border-fg',
                      )}
                    >
                      {paymentMethodLabel(m)}
                    </button>
                  ))}
                </div>
              </div>

              {method === 'credit' ? (
                pickerOpen && !locked ? (
                  <CustomerPicker
                    onSelect={(c) => {
                      setPickedCustomer(c);
                      setPickerOpen(false);
                    }}
                    onCancel={() => {
                      setPickerOpen(false);
                      if (!customer) setMethod('cash');
                    }}
                  />
                ) : customer ? (
                  <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2 text-sm">
                    <span className="flex min-w-0 items-center gap-2">
                      <UserRound className="size-4 shrink-0" />
                      <span className="truncate">
                        A crédito de <strong>{customer.fullName}</strong>
                        {customer.balanceCents != null && customer.balanceCents > 0
                          ? ` · saldo actual ${formatCOP(customer.balanceCents)}`
                          : ''}
                      </span>
                    </span>
                    {!locked ? (
                      <Button variant="ghost" size="sm" onClick={() => setPickerOpen(true)}>
                        Cambiar
                      </Button>
                    ) : null}
                  </div>
                ) : null
              ) : null}

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label htmlFor="pay-amount" className="text-sm font-semibold">
                    Monto a cobrar
                  </label>
                  <div className="flex gap-1">
                    {SPLITS.map((s) => (
                      <button
                        key={s.label}
                        type="button"
                        onClick={() => splitAmount(s.divisor)}
                        className="rounded-full border border-border bg-surface px-2 py-0.5 text-xs font-semibold text-fg-muted hover:border-fg hover:text-fg"
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <input
                  id="pay-amount"
                  type="text"
                  inputMode="numeric"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                  placeholder={formatCOP(remaining)}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-lg font-bold"
                />
                {exceeds ? (
                  <p className="mt-1 text-xs text-danger">
                    El monto excede lo pendiente ({formatCOP(remaining)}).
                  </p>
                ) : null}
              </div>

              {method === 'cash' ? (
                <div>
                  <label htmlFor="pay-received" className="mb-2 block text-sm font-semibold">
                    Efectivo recibido (opcional)
                  </label>
                  <input
                    id="pay-received"
                    type="text"
                    inputMode="numeric"
                    value={receivedInput}
                    onChange={(e) => setReceivedInput(e.target.value)}
                    placeholder="Para calcular el vuelto"
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base font-semibold"
                  />
                  {receivedCents > 0 && !exceeds ? (
                    changeCents > 0 ? (
                      <p className="mt-2 rounded-md bg-success/10 px-3 py-2 text-sm">
                        Vuelto <strong className="tabular-nums">{formatCOP(changeCents)}</strong>
                      </p>
                    ) : receivedCents < baseAmount ? (
                      <p className="mt-2 rounded-md bg-surface-muted px-3 py-2 text-sm">
                        Se registra un abono de {formatCOP(receivedCents)} · quedan{' '}
                        {formatCOP(remaining - receivedCents)} por cobrar
                      </p>
                    ) : null
                  ) : null}
                </div>
              ) : null}

              {createPayment.error ? (
                <p className="text-sm text-danger">{createPayment.error.message}</p>
              ) : null}

              <Button size="lg" className="w-full" disabled={!canSubmit} onClick={submit}>
                <CreditCard className="size-4" />
                {createPayment.isPending
                  ? 'Registrando…'
                  : method === 'credit'
                    ? `Vender a crédito ${formatCOP(amountToCharge)}`
                    : `Registrar pago ${formatCOP(amountToCharge)}`}
              </Button>
              <p className="text-center text-xs text-fg-muted">
                Puedes combinar medios de pago o cerrar y registrar el resto después.
              </p>
            </>
          ) : (
            <>
              {transition.error ? (
                <p className="text-sm text-danger">{transition.error.message}</p>
              ) : null}
              <Button
                size="lg"
                className="w-full"
                disabled={transition.isPending}
                onClick={() => transition.mutate({ orderId, to: 'paid' })}
              >
                <Check className="size-4" />
                {transition.isPending ? 'Cerrando…' : 'Finalizar cobro'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryLine = ({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) => (
  <div className="flex items-baseline justify-between">
    <span className="text-fg-muted">{label}</span>
    <span className={cn('tabular-nums', strong && 'font-bold')}>{value}</span>
  </div>
);
